/**
 * Port Status Monitor Panel – same grid as Port Status Monitor page, read-only.
 * For use on the user gateway page: shows only the user's gateway; refreshes when Refresh is clicked.
 */
import React, { useState, useCallback } from 'react';
import { fetchPortStatusCached, invalidateGatewayStatusCache } from '../../../api';

function createGrid(statusArray, maxPorts = 64, maxSlots = 8) {
  const grid = {};
  for (let p = 1; p <= maxPorts; p++) {
    grid[p] = {};
    for (let s = 1; s <= maxSlots; s++) {
      grid[p][s] = { active: 0, data: null };
    }
  }
  statusArray.forEach((slot) => {
    const [portNum, slotNum] = String(slot.port || '').split('.');
    const p = parseInt(portNum, 10);
    const s = parseInt(slotNum, 10);
    if (p >= 1 && p <= maxPorts && s >= 1 && s <= maxSlots) {
      grid[p][s] = {
        active: slot.active || (slot.st === 3 || slot.st === 4 ? 1 : 0),
        data: slot,
      };
    }
  });
  return grid;
}

function renderGridTable(grid, maxPorts = 64, maxSlots = 8) {
  return (
    <div style={{ overflowX: 'auto', maxHeight: '500px' }}>
      <table
        style={{
          borderCollapse: 'collapse',
          fontSize: '10px',
          minWidth: '100%',
        }}
      >
        <thead>
          <tr>
            <th
              style={{
                padding: '4px 2px',
                border: '1px solid #e0e0e0',
                background: '#f0f0f0',
                position: 'sticky',
                top: 0,
              }}
            />
            {Array.from({ length: maxPorts }, (_, i) => i + 1).map((p) => (
              <th
                key={`port-${p}`}
                style={{
                  padding: '4px 2px',
                  border: '1px solid #e0e0e0',
                  background: '#f0f0f0',
                  fontWeight: 600,
                  fontSize: '8px',
                  minWidth: '26px',
                  position: 'sticky',
                  top: 0,
                }}
              >
                {p}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: maxSlots }, (_, i) => i + 1).map((slot) => (
            <tr key={`slot-${slot}`}>
              <td
                style={{
                  padding: '4px 2px',
                  border: '1px solid #e0e0e0',
                  background: '#f5f5f5',
                  fontWeight: 600,
                  color: '#667eea',
                  fontSize: '9px',
                  minWidth: '45px',
                  position: 'sticky',
                  left: 0,
                  zIndex: 5,
                }}
              >
                .{String(slot).padStart(2, '0')}
              </td>
              {Array.from({ length: maxPorts }, (_, i) => i + 1).map((port) => {
                const slotData = grid[port]?.[slot];
                const isActive = slotData?.active === 1;
                const operator = slotData?.data?.opr || '';
                const signal = slotData?.data?.sig || 0;
                const portStr = `${port}.${String(slot).padStart(2, '0')}`;
                return (
                  <td
                    key={`${port}-${slot}`}
                    style={{
                      padding: '4px 2px',
                      border: '1px solid #e0e0e0',
                      textAlign: 'center',
                      height: '32px',
                    }}
                    title={isActive ? `${operator}\nSignal: ${signal}/31` : ''}
                  >
                    <div
                      style={{
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        margin: '0 auto',
                        background: isActive ? '#51cf66' : '#d0d0d0',
                        color: isActive ? 'white' : '#666',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '7px',
                        fontWeight: 600,
                        boxShadow: isActive ? '0 0 8px rgba(81, 207, 102, 0.6)' : 'none',
                      }}
                    />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function PortStatusMonitorPanel({ gatewayId, gatewayName, isOpen, onToggle, onStatusUpdate }) {
  const [statusArray, setStatusArray] = useState([]);
  const [maxPorts, setMaxPorts] = useState(64);
  const [maxSlots, setMaxSlots] = useState(8);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  const refreshStatus = useCallback(async () => {
    if (!gatewayId) return;
    const gwKey = typeof gatewayId === 'string' && gatewayId.startsWith('gw-') ? gatewayId : `gw-${gatewayId}`;
    setLoading(true);
    setError(null);
    try {
      invalidateGatewayStatusCache(gwKey);
      const response = await fetchPortStatusCached(gwKey);
      if (response?.error) {
        setError(response.error);
        setStatusArray([]);
        if (onStatusUpdate) onStatusUpdate([]);
      } else {
        const arr = response?.status || [];
        setStatusArray(arr);
        if (onStatusUpdate) onStatusUpdate(arr);
        setLastUpdate(new Date().toLocaleTimeString());
        const mp = response['max-ports'] != null ? Number(response['max-ports']) : 64;
        const ms = response['max-slot'] != null ? Number(response['max-slot']) : 8;
        setMaxPorts(mp);
        setMaxSlots(ms);
      }
    } catch (err) {
      setError(err?.message || 'Failed to load status');
      setStatusArray([]);
      if (onStatusUpdate) onStatusUpdate([]);
    } finally {
      setLoading(false);
    }
  }, [gatewayId, onStatusUpdate]);

  const grid = createGrid(statusArray, maxPorts, maxSlots);

  return (
    <div className="card mb-3">
      <div
        className="card-header d-flex justify-content-between align-items-center py-2"
        style={{ cursor: 'pointer', backgroundColor: '#e8f0fe', borderLeft: '4px solid #1a73e8' }}
        onClick={onToggle}
      >
        <strong style={{ fontSize: '15px' }}>
          {isOpen ? '▼' : '▶'} Port Status Monitor
          {gatewayName && <span className="text-muted ms-2">({gatewayName})</span>}
        </strong>
        <span className="text-muted small">{isOpen ? 'Click to collapse' : 'Click to view gateway port grid'}</span>
      </div>
      {isOpen && (
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <span className="text-muted small">
              Total slots: {statusArray.length}
              {lastUpdate && ` · Last updated: ${lastUpdate}`}
            </span>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={(e) => {
                e.stopPropagation();
                refreshStatus();
              }}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Refreshing...
                </>
              ) : (
                '🔄 Refresh'
              )}
            </button>
          </div>
          {error && (
            <div className="alert alert-danger py-2 mb-3">
              <strong>Error:</strong> {error}
            </div>
          )}
          {!error && statusArray.length > 0 && (
            <div
              style={{
                background: '#f9f9f9',
                padding: '12px',
                borderRadius: '8px',
                border: '1px solid #ddd',
              }}
            >
              {renderGridTable(grid, maxPorts, maxSlots)}
            </div>
          )}
          {!error && statusArray.length === 0 && !loading && (
            <p className="text-muted mb-0">Click <strong>Refresh</strong> to load port status.</p>
          )}
        </div>
      )}
    </div>
  );
}
